import React, { ReactNode } from 'react';
import ImageLoader from './ImageLoader';

interface PageHeroProps {
  title: string;
  subtitle?: string;
  backgroundImage?: string;
  height?: string;
}

interface ImageGalleryProps {
  images: {
    src: string;
    alt: string;
  }[];
  columns?: number;
}

interface SectionProps {
  title?: string;
  subtitle?: string;
  children: ReactNode;
  background?: 'white' | 'light' | 'primary' | 'accent';
  textColor?: string;
  textAlign?: 'left' | 'center' | 'right';
  paddingY?: string;
}

interface CardLayoutProps {
  children: ReactNode;
  columns?: number;
  gap?: string;
}

// Composant pour les héros de page (bannières principales)
export const PageHero: React.FC<PageHeroProps> = ({ 
  title, 
  subtitle, 
  backgroundImage = '/default-hero.jpg',
  height = '50vh' 
}) => {
  return (
    <div 
      className="bg-cover bg-center relative" 
      style={{ 
        height
      }}
    >
      <div className="absolute inset-0 -z-10">
        <ImageLoader 
          src={backgroundImage}
          alt="Background hero"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-black bg-opacity-40" />
      <div className="absolute inset-0 flex items-center justify-center text-center">
        <div className="text-white max-w-4xl px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{title}</h1>
          {subtitle && (
            <p className="text-xl md:text-2xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Composant pour les sections de page
export const Section: React.FC<SectionProps> = ({ 
  title, 
  subtitle, 
  children, 
  background = 'white',
  textColor,
  textAlign = 'left',
  paddingY = '16'
}) => {
  const getBgColor = () => {
    switch (background) {
      case 'light': return 'bg-light';
      case 'primary': return 'bg-primary';
      case 'accent': return 'bg-accent';
      default: return 'bg-white';
    }
  };

  const getTextColor = () => {
    if (textColor) return textColor;
    if (background === 'primary') return 'text-white';
    if (background === 'accent') return 'text-dark';
    return 'text-dark';
  };

  const getTextAlign = () => {
    switch (textAlign) {
      case 'center': return 'text-center';
      case 'right': return 'text-right';
      default: return 'text-left';
    }
  };

  return (
    <section className={`py-${paddingY} ${getBgColor()}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {(title || subtitle) && (
          <div className={`max-w-4xl mx-auto mb-10 ${getTextAlign()}`}>
            {title && (
              <h2 className={`text-3xl font-bold mb-4 ${background === 'primary' ? 'text-white' : 'text-primary'}`}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`text-lg ${getTextColor()}`}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        <div className={getTextColor()}>
          {children}
        </div>
      </div>
    </section>
  );
};

// Mise en page pour les cartes (services, équipements, etc.)
export const CardLayout: React.FC<CardLayoutProps> = ({ 
  children, 
  columns = 3,
  gap = '8'
}) => {
  const getGridCols = () => {
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 4: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
      default: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }
  };

  return (
    <div className={`grid ${getGridCols()} gap-${gap}`}>
      {children}
    </div>
  );
};

// Carte basique réutilisable
export const Card: React.FC<{children: ReactNode; shadow?: 'sm' | 'md' | 'lg'; className?: string}> = ({ 
  children, 
  shadow = 'md', 
  className = ''
}) => {
  const getShadow = () => {
    switch (shadow) {
      case 'sm': return 'shadow-sm';
      case 'lg': return 'shadow-lg';
      default: return 'shadow-md';
    }
  };

  return (
    <div className={`bg-white rounded-lg p-6 ${getShadow()} ${className}`}>
      {children}
    </div>
  );
};

// Composant pour les galeries d'images
export const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images, 
  columns = 3 
}) => {
  const getGridCols = () => {
    switch (columns) {
      case 1: return 'grid-cols-1';
      case 2: return 'grid-cols-1 md:grid-cols-2';
      case 4: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
      default: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
    }
  };

  return (
    <div className={`grid ${getGridCols()} gap-4`}>
      {images.map((image, index) => (
        <div key={index} className="overflow-hidden rounded-lg shadow-md group">
          <div className="relative aspect-[4/3] overflow-hidden">
            <div className="absolute inset-0 transform group-hover:scale-110 transition-transform duration-500">
              <ImageLoader 
                src={image.src} 
                alt={image.alt} 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Bouton d'appel à l'action
export const CtaButton: React.FC<{
  children: ReactNode;
  to: string;
  type?: 'primary' | 'secondary' | 'accent' | 'outline';
  className?: string;
}> = ({ 
  children, 
  to, 
  type = 'primary',
  className = ''
}) => {
  const getButtonClass = () => {
    switch (type) {
      case 'secondary':
        return 'bg-secondary text-white hover:bg-secondary/90';
      case 'accent':
        return 'bg-accent text-dark hover:bg-accent/90';
      case 'outline':
        return 'bg-transparent border-2 border-white text-white hover:bg-white/10';
      default:
        return 'bg-primary text-white hover:bg-primary/90';
    }
  };

  return (
    <a 
      href={to} 
      className={`inline-block px-6 py-3 font-medium rounded-md transition-colors ${getButtonClass()} ${className}`}
    >
      {children}
    </a>
  );
};

// Conteneur d'image avec aspect ratio fixe
export const ImageContainer: React.FC<{
  src: string;
  alt: string;
  aspectRatio?: string; // e.g., '16/9', '4/3', '1/1', '3/4'
  rounded?: boolean;
  className?: string;
}> = ({
  src,
  alt,
  aspectRatio = '16/9', // Default aspect ratio
  rounded = true,
  className = ''
}) => {

  // Helper function to map prop value to Tailwind class
  const getAspectRatioClass = (ratio: string): string => {
    switch (ratio) {
      case '1/1': return 'aspect-square';
      case '16/9': return 'aspect-video';
      case '4/3': return 'aspect-[4/3]';
      case '3/4': return 'aspect-[3/4]';
      // Add other common ratios if needed
      default:
        // Attempt to use arbitrary value if format matches 'x/y'
        if (/^\d+\/\d+$/.test(ratio)) {
          return `aspect-[${ratio}]`;
        }
        return 'aspect-video'; // Default fallback
    }
  };

  const aspectRatioClass = getAspectRatioClass(aspectRatio);

  return (
    <div 
      className={`overflow-hidden ${aspectRatioClass} ${rounded ? 'rounded-lg' : ''} ${className}`}
    >
      <ImageLoader 
        src={src} 
        alt={alt} 
        className="w-full h-full object-cover" 
      />
    </div>
  );
};

// Exporter les composants pour une utilisation facile
export { Section as SectionContainer, Card as CardContainer, ImageGallery as Gallery }; 